import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { useEdiPasswordReset } from '@/api/hanly-v2/edi/edi';
import { Button, LinkButton } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { emailSchema } from '@/utils/schemas';

const passwordResetSchema = z.object({
  email: emailSchema,
});

type PasswordResetFormData = z.infer<typeof passwordResetSchema>;

/** パスワード再設定メールの送信。アカウントの有無を推測させないよう、API は登録の有無にかかわらず成功を返す */
export const PasswordResetPage = () => {
  const [sentEmail, setSentEmail] = useState('');
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetSchema),
  });

  const { mutate: requestReset, isPending } = useEdiPasswordReset();

  const onSubmit = (data: PasswordResetFormData) => {
    setApiError('');
    requestReset(
      { data: { email: data.email } },
      {
        onSuccess: () => setSentEmail(data.email),
        onError: (error) => {
          setApiError(
            error.status === 429
              ? '短時間に送信が繰り返されました。しばらく時間をおいてから再度お試しください。'
              : 'メールの送信に失敗しました。時間をおいて再度お試しください。',
          );
        },
      },
    );
  };

  return (
    <div className="flex w-full flex-col gap-6 px-6 py-14 md:max-w-md md:px-0">
      <div className="flex flex-col gap-1">
        <h2>パスワードの再設定</h2>
        {!sentEmail && (
          <p className="text-neutral-600">
            ご登録のメールアドレスを入力してください。パスワードを再設定するためのリンクをお送りします。
          </p>
        )}
      </div>

      {sentEmail ? (
        <div className="flex flex-col gap-5">
          <p className="flex gap-2">
            <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-sky-800" />
            <span>
              <span className="font-bold break-all">{sentEmail}</span>
              宛に、パスワード再設定のメールを送信しました。メールのリンクから1時間以内に新しいパスワードを設定してください。
            </span>
          </p>
          <p className="text-sm text-neutral-600">
            数分経ってもメールが届かない場合は、メールアドレスに誤りがないかをご確認のうえ、再度お試しください。
          </p>
          <LinkButton to="/signin" variant="outlined" size="lg" fullWidth className="text-base">
            ログイン画面に戻る
          </LinkButton>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-5">
          {apiError && (
            <p role="alert" className="flex gap-2 font-bold text-red-700">
              <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" />
              {apiError}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="font-bold">
              メールアドレス
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="例）tanaka@example.co.jp"
              aria-invalid={errors.email ? true : undefined}
              className="h-12 text-base! md:h-11"
              {...register('email')}
            />
            <FormError message={errors.email?.message} />
          </div>
          <Button type="submit" size="lg" fullWidth loading={isPending} className="mt-1 text-base">
            再設定メールを送信
          </Button>
          <Link
            to="/signin"
            className="text-center text-sm text-neutral-600 underline hover:text-neutral-900"
          >
            ログイン画面に戻る
          </Link>
        </form>
      )}
    </div>
  );
};
